<template>
  <section class="relative pt-24 pb-16 md:pt-28 md:pb-24 bg-[#1E2652]">
    <div class="container mx-auto px-4 md:px-6 max-w-6xl">
      <!-- Header -->
      <div class="mb-10 md:mb-14 text-center">
        <h1 class="text-3xl md:text-4xl font-bold text-white tracking-tight">
          Conditions Générales de Vente (CGV)
        </h1>
        <div class="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 text-gray-200 px-3 py-1 text-sm">
          <span class="h-2 w-2 rounded-full bg-[#B7E000]"></span>
          <span>Dernière mise à jour : 7 septembre 2025</span>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
        <!-- TOC -->
        <aside class="hidden md:block md:col-span-4 lg:col-span-3">
          <nav class="sticky top-24 bg-white/5 border border-white/10 rounded-xl p-4">
            <h2 class="text-sm font-semibold text-gray-200 mb-3">Sommaire</h2>
            <ul class="space-y-2">
              <li v-for="item in toc" :key="item.slug">
                <a :href="'#' + item.slug" class="toc-link block text-sm text-gray-300 hover:text-white">
                  {{ item.title }}
                </a>
              </li>
            </ul>
          </nav>
        </aside>

        <!-- Content -->
        <main class="md:col-span-8 lg:col-span-9">
          <div class="bg-white/[0.04] border border-white/10 rounded-2xl p-6 md:p-8">
            <section
              v-for="(section, index) in cgvSections"
              :key="index"
              class="pt-4 first:pt-0 border-t border-white/10 first:border-t-0"
            >
              <h2
                class="text-xl md:text-2xl font-semibold text-[#B7E000] mb-3 scroll-mt-28"
                :id="toc[index].slug"
              >
                {{ section.title }}
              </h2>
              <div class="text-gray-200 leading-relaxed space-y-4" v-html="section.content"></div>
            </section>
          </div>

          <!-- Contact Section -->
          <div class="text-center mt-10">
            <h3 class="text-lg font-semibold text-white mb-3">Contact</h3>
            <p class="text-gray-300 mb-5">Pour toute question ou réclamation, veuillez nous contacter à l'adresse suivante :</p>
            <a href="mailto:support@padelgo.ma" class="inline-flex items-center px-6 py-3 rounded-full bg-[#B7E000] text-[#1E2652] font-medium hover:bg-[#B7E000]/90 transition-colors">support@padelgo.ma</a>
          </div>
        </main>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">

const cgvSections = [
  {
    title: "AVERTISSEMENT",
    content: `
      <p>Vous venez de vous connecter sur le site padelgo.ma et/ou l'Application PadelGo, éditée par la société RAYAN MARSILE CORP.</p>
      <p>Vous devez lire attentivement les dispositions qui suivent car elles constituent un contrat établissant les conditions générales applicables à l'utilisation des services proposés par l'Application.</p>
      <p>Le « clic » que vous exécutez après avoir validé une réservation constitue la confirmation définitive de votre commande et vaut acceptation irrévocable des présentes conditions générales.</p>
      <p>En conséquence, vous ne pouvez accéder aux services proposés que si vous acceptez l'ensemble des dispositions prévues ci-après.</p>
    `
  },
  {
    title: "1. Préambule",
    content: `
      <p>Les présentes conditions générales de vente régissent l'ensemble des transactions réalisées sur l'Application PadelGo, éditée par la société RAYAN MARSILE CORP. Toute utilisation de l'Application suppose l'acceptation pleine et entière de ces conditions.</p>
    `
  },
  {
    title: "2. Objet",
    content: `
      <p>Le présent contrat définit les droits et obligations des parties dans le cadre de l'utilisation des services de réservation de terrains sur l'Application PadelGo et des options associées (mise en relation, tournois, cours, fonctionnalités communautaires).</p>
    `
  },
  {
    title: "3. Définitions",
    content: `
      <p><strong>Contrat à distance :</strong> toute réservation effectuée entre l'Utilisateur et la société RAYAN MARSILE CORP via l'Application PadelGo, sans présence physique des parties.</p>
      <p><strong>Application :</strong> PadelGo, application mobile éditée par la Société RAYAN MARSILE CORP.</p>
      <p><strong>Utilisateur :</strong> toute personne physique utilisant l'Application.</p>
      <p><strong>Commande :</strong> toute réservation d'un service (terrain, match, cours, tournoi) via l'Application.</p>
      <p><strong>Club partenaire :</strong> établissement sportif référençant ses terrains et créneaux sur l'Application.</p>
    `
  },
  {
    title: "4. Services proposés",
    content: `
      <p>Les services proposés incluent (liste non exhaustive) :</p>
      <ul class="list-disc list-inside pl-4 mb-4">
        <li>Réservation de terrains de padel.</li>
        <li>Organisation et confirmation de matchs.</li>
        <li>Mise en relation avec d'autres joueurs.</li>
        <li>Accès à des tournois, cours et fonctionnalités communautaires.</li>
      </ul>
    `
  },
  {
    title: "5. Passer une commande",
    content: `
      <p>Toute réservation via l'Application constitue une commande engageante, sous réserve des conditions d'annulation prévues à l'article 8.2.</p>
      <p>En cas d'erreur, l'Utilisateur peut contacter le support client à l'adresse : support@padelgo.ma.</p>
    `
  },
  {
    title: "6. Prix",
    content: `
      <p>Les prix sont indiqués en dirhams marocains (MAD), toutes taxes comprises (TTC). Ils sont ceux en vigueur au moment de la commande.</p>
      <p>La société RAYAN MARSILE CORP se réserve le droit de modifier ses prix à tout moment.</p>
    `
  },
  {
    title: "7. Exécution de la commande",
    content: `
      <p>La commande est confirmée une fois le processus de réservation complété via l'Application.</p>
      <p>Que le paiement soit effectué en ligne ou en espèces auprès du Club partenaire, la réservation est transmise au Club partenaire concerné.</p>
      <p>Le Club partenaire reste seul responsable de la bonne exécution du service réservé (mise à disposition du terrain, respect des horaires, etc.).</p>
      <p>En cas d'indisponibilité du service réservé (terrain fermé, erreur de planning, etc.), l'Utilisateur sera informé dès que possible.</p>
      <p>Si le paiement a été effectué en ligne, l'Utilisateur pourra demander le remboursement intégral.</p>
      <p>Si le paiement doit être réglé en espèces sur place, aucune somme ne sera due par l'Utilisateur.</p>
    `
  },
  {
    title: "8. Droit de rétractation et annulation",
    content: `
      <h3 class="text-lg font-semibold text-[#B7E000] mb-2 mt-4">8.1 – Droit de rétractation</h3>
      <p>Conformément à l'article 38 de la loi marocaine n°31-08 relative à la protection du consommateur, le droit de rétractation ne peut pas être exercé pour les contrats de fourniture de services dont l'exécution a commencé, avec l'accord du consommateur, avant la fin du délai légal de 7 jours.</p>
      <p>Les réservations de terrains de padel, qui constituent des prestations de loisirs fournies à une date précise, entrent dans ce cadre.</p>
      <p>Ainsi, toute réservation effectuée via l'Application est engageante, sous réserve des conditions d'annulation définies ci-après.</p>
      
      <h3 class="text-lg font-semibold text-[#B7E000] mb-2 mt-4">8.2 – Politique d'annulation (dernière mise à jour : 1er août 2025)</h3>
      
      <h4 class="font-semibold text-white mb-2 mt-3">Annulation par les joueurs</h4>
      <p>Un match confirmé ou une réservation peuvent être annulés jusqu'à 1 heure avant le début du match.</p>
      <p>Au-delà de ce délai, aucun remboursement ou report n'est possible, sauf en cas de force majeure dûment justifiée.</p>
      
      <h4 class="font-semibold text-white mb-2 mt-3">Annulation par le club</h4>
      <p>Le Club partenaire peut annuler une réservation ou un match à tout moment.</p>
      <p>Dans ce cas, l'Utilisateur est intégralement remboursé si le paiement a été effectué en ligne.</p>
      
      <h4 class="font-semibold text-white mb-2 mt-3">Annulation automatique</h4>
      <p>Un match est automatiquement annulé à l'heure prévue s'il n'a pas été confirmé.</p>
      
      <h4 class="font-semibold text-white mb-2 mt-3">Non-présentation</h4>
      <p>En cas d'absence du joueur sans annulation préalable conforme aux conditions ci-dessus, le montant payé reste acquis au Club partenaire.</p>
    `
  },
  {
    title: "9. Paiement",
    content: `
      <p>Les paiements sont sécurisés par le Centre Monétique Interbancaire (CMI).</p>
      <p>L'Utilisateur garantit disposer des autorisations nécessaires pour utiliser le mode de paiement choisi.</p>
      <p>En cas de paiement par carte bancaire, les dispositions relatives à l'utilisation frauduleuse prévues par la réglementation bancaire s'appliquent.</p>
      <p>L'Utilisateur peut également choisir de régler en espèces directement auprès du Club partenaire.</p>
    `
  },
  {
    title: "10. Confidentialité des données",
    content: `
      <p>Les données personnelles collectées sont traitées conformément à la loi marocaine n°09-08.</p>
      <p>L'Utilisateur dispose d'un droit d'accès, de rectification et de suppression de ses données en adressant une demande à support@padelgo.ma.</p>
    `
  },
  {
    title: "11. Preuve des transactions",
    content: `
      <p>Les données enregistrées par le CMI constituent la preuve de l'ensemble des transactions passées entre l'Utilisateur et la société RAYAN MARSILE CORP.</p>
    `
  },
  {
    title: "12. Force majeure",
    content: `
      <p>La société RAYAN MARSILE CORP ne saurait être tenue responsable de l'inexécution totale ou partielle de ses obligations en cas de force majeure.</p>
    `
  }
]

function slugifyTitle(title: string): string {
  return title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

const toc = cgvSections.map((s) => ({ title: s.title, slug: slugifyTitle(s.title) }))
</script>

<style scoped>
.toc-link {
  transition: color 0.15s ease;
}
</style>